import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  dailyRecordsAPI,
  personnelAPI,
  projectsAPI,
  vehiclesAPI,
  workTypesAPI,
} from '../../helpers/api';
import { useAuth } from '../../helpers/hooks';
import BottomSheet from '../../components/Common/BottomSheet';

interface SiteWork {
  id: string;
  targetQuantity: number | null;
  completedQuantity: number;
  progressPercentage: number | null;
  status: string;
  workType: {
    id: string;
    name: string;
    unit: string;
  };
}

type Step = 'personel' | 'isler' | 'arac' | 'notlar';
const STEPS: Step[] = ['personel', 'isler', 'arac', 'notlar'];
const STEP_LABELS: Record<Step, string> = {
  personel: 'Personel',
  isler: 'İşler',
  arac: 'Araçlar',
  notlar: 'Notlar',
};

const NO_WORK_REASONS = [
  { value: 'RAIN', label: 'Yağmur / Kar' },
  { value: 'HOLIDAY', label: 'Resmi Tatil' },
  { value: 'EQUIPMENT', label: 'Ekipman Arızası' },
  { value: 'MATERIAL', label: 'Malzeme Bekleniyor' },
  { value: 'PERMIT', label: 'İzin / Ruhsat Bekleniyor' },
  { value: 'POWER', label: 'Elektrik / Su Kesintisi' },
  { value: 'OTHER', label: 'Diğer' },
];

const FormanDailyRecord: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>('personel');
  const [submitting, setSubmitting] = useState(false);
  const [showSiteSheet, setShowSiteSheet] = useState(false);
  const [showPersonnelSheet, setShowPersonnelSheet] = useState(false);
  const [showVehicleSheet, setShowVehicleSheet] = useState(false);
  const [showDriverSheet, setShowDriverSheet] = useState<string | null>(null);
  const [showNoWorkSheet, setShowNoWorkSheet] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    recordDate: new Date().toISOString().split('T')[0],
    siteId: '',
    notes: '',
  });

  // Options
  const [sites, setSites] = useState<any[]>([]);
  const [sitePersonnel, setSitePersonnel] = useState<any[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [allVehicles, setAllVehicles] = useState<any[]>([]);
  const [siteWorks, setSiteWorks] = useState<SiteWork[]>([]);
  const [loadingSiteWorks, setLoadingSiteWorks] = useState(false);

  // Eksik gün kontrolü
  const [missingDays, setMissingDays] = useState<string[]>([]);

  // Kayıt kilidi durumu
  const [existingRecord, setExistingRecord] = useState<{
    id: string;
    isCompleted: boolean;
    createdBy: { firstName: string; lastName: string };
  } | null>(null);
  const [checkingRecord, setCheckingRecord] = useState(false);

  // Selections
  const [selectedPersonnel, setSelectedPersonnel] = useState<
    Array<{ personnelId: string; dailyWage: number; overtimeHours?: number }>
  >([]);
  const [showOvertimeSheet, setShowOvertimeSheet] = useState<string | null>(null);
  const [selectedVehicles, setSelectedVehicles] = useState<
    Array<{ vehicleId: string; usageType: string; driverId: string; driverType: string; notes: string; km: number | null; hours: number | null }>
  >([]);
  const [selectedWorks, setSelectedWorks] = useState<
    Array<{ siteWorkId: string; quantity: number; notes: string }>
  >([]);
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  useEffect(() => {
    fetchSites();
    fetchVehicles();
  }, []);

  // Şantiye seçildiğinde o şantiyenin işlerini, personelini ve eksik günleri getir
  useEffect(() => {
    if (formData.siteId) {
      fetchSiteWorks(formData.siteId);
      fetchSitePersonnel(formData.siteId);
      // Eksik gün kontrolü
      dailyRecordsAPI.checkMissingDays(formData.siteId).then((res) => {
        // Backend Date[] döner → NestJS ISO string'e çevirir (ör: "2026-01-15T00:00:00.000Z")
        const days: string[] = (res.data as string[]).map((d) => d.split('T')[0]);
        setMissingDays(days);
        if (days.length > 0) {
          // Tarihi ilk eksik güne otomatik set et
          setFormData((prev) => ({ ...prev, recordDate: days[0] }));
        }
      }).catch(() => {
        setMissingDays([]);
      });
    } else {
      setSiteWorks([]);
      setSitePersonnel([]);
      setSelectedWorks([]);
      setSelectedPersonnel([]);
      setMissingDays([]);
    }
  }, [formData.siteId]);

  // Şantiye veya tarih değiştiğinde kayıt var mı kontrol et
  useEffect(() => {
    if (formData.siteId && formData.recordDate) {
      checkExistingRecord(formData.siteId, formData.recordDate);
    } else {
      setExistingRecord(null);
    }
  }, [formData.siteId, formData.recordDate]);

  const checkExistingRecord = async (siteId: string, date: string) => {
    try {
      setCheckingRecord(true);
      const response = await dailyRecordsAPI.checkRecordExists(siteId, date);
      setExistingRecord(response.data);
    } catch (error: any) {
      // 404 = kayıt yok, sorun değil
      if (error.response?.status === 404) {
        setExistingRecord(null);
      } else {
        console.error('Record check error:', error);
      }
    } finally {
      setCheckingRecord(false);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await projectsAPI.getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Sites fetch error:', error);
    }
  };

  const fetchSitePersonnel = async (_siteId: string) => {
    try {
      setLoadingPersonnel(true);
      const response = await personnelAPI.getAll({ status: 'ACTIVE' });
      setSitePersonnel(response.data);
    } catch (error) {
      console.error('Personnel fetch error:', error);
      setSitePersonnel([]);
    } finally {
      setLoadingPersonnel(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await vehiclesAPI.getAll();
      setAllVehicles(response.data.filter((v: any) => v.isActive));
    } catch (error) {
      console.error('Vehicles fetch error:', error);
    }
  };

  const fetchSiteWorks = async (siteId: string) => {
    try {
      setLoadingSiteWorks(true);
      const response = await workTypesAPI.getSiteWorks(siteId);
      // Sadece IN_PROGRESS ve NOT_STARTED durumundaki işleri göster
      setSiteWorks(response.data.filter((sw: SiteWork) => sw.status !== 'COMPLETED'));
    } catch (error) {
      console.error('Site works fetch error:', error);
    } finally {
      setLoadingSiteWorks(false);
    }
  };

  const togglePersonnel = (person: any) => {
    // İzinli/raporlu personel seçilemez
    if (person.status === 'ON_LEAVE' || person.status === 'ON_REPORT') return;

    const exists = selectedPersonnel.find((p) => p.personnelId === person.id);
    if (exists) {
      setSelectedPersonnel(selectedPersonnel.filter((p) => p.personnelId !== person.id));
    } else {
      setSelectedPersonnel([...selectedPersonnel, { personnelId: person.id, dailyWage: 1 }]);
    }
  };

  // BottomSheet için personel seçenekleri
  const personnelOptions = useMemo(() => {
    const monthly = sitePersonnel.filter((p: any) => p.employmentType === 'MONTHLY');
    const daily = sitePersonnel.filter((p: any) => p.employmentType !== 'MONTHLY');

    const mapPerson = (p: any, group: string) => ({
      value: p.id,
      label: `${p.firstName} ${p.lastName}`,
      sublabel: p.title || 'İşçi',
      group,
      disabled: p.status === 'ON_LEAVE' || p.status === 'ON_REPORT',
      badge: p.status === 'ON_LEAVE' ? 'İzinli' : p.status === 'ON_REPORT' ? 'Raporlu' : undefined,
    });

    return [
      ...monthly.map((p: any) => mapPerson(p, 'Aylıkçı Personel')),
      ...daily.map((p: any) => mapPerson(p, 'Yevmiyeci Personel')),
    ];
  }, [sitePersonnel]);

  const handlePersonnelSheetSelect = (personnelId: string) => {
    const person = sitePersonnel.find((p: any) => p.id === personnelId);
    if (person) togglePersonnel(person);
  };

  const updatePersonnelWage = (personnelId: string, dailyWage: number) => {
    setSelectedPersonnel(
      selectedPersonnel.map((p) =>
        p.personnelId === personnelId ? { ...p, dailyWage } : p
      )
    );
  };

  // Aylıkçı mesai modu: 0.5=yarım, 1=tam gün, 'overtime'=tam gün + mesai
  const setMonthlyMode = (personnelId: string, mode: 'half' | 'full' | 'overtime') => {
    if (mode === 'overtime') {
      // Mesai sheet'i aç
      setShowOvertimeSheet(personnelId);
    } else {
      setSelectedPersonnel(
        selectedPersonnel.map((p) =>
          p.personnelId === personnelId
            ? { ...p, dailyWage: mode === 'half' ? 0.5 : 1, overtimeHours: undefined }
            : p
        )
      );
    }
  };

  const setOvertimeHours = (personnelId: string, hours: number) => {
    setSelectedPersonnel(
      selectedPersonnel.map((p) =>
        p.personnelId === personnelId
          ? { ...p, dailyWage: 1, overtimeHours: hours }
          : p
      )
    );
    setShowOvertimeSheet(null);
  };

  const getMonthlyMode = (personnelId: string): 'half' | 'full' | 'overtime' => {
    const p = selectedPersonnel.find((sp) => sp.personnelId === personnelId);
    if (!p) return 'full';
    if (p.overtimeHours && p.overtimeHours > 0) return 'overtime';
    if (p.dailyWage === 0.5) return 'half';
    return 'full';
  };

  const getOvertimeHours = (personnelId: string): number => {
    return selectedPersonnel.find((p) => p.personnelId === personnelId)?.overtimeHours || 0;
  };

  const toggleVehicle = (vehicleId: string) => {
    const exists = selectedVehicles.find((v) => v.vehicleId === vehicleId);
    if (exists) {
      setSelectedVehicles(selectedVehicles.filter((v) => v.vehicleId !== vehicleId));
    } else {
      setSelectedVehicles([
        ...selectedVehicles,
        { vehicleId, usageType: 'FULL_DAY', driverId: '', driverType: '', notes: '', km: null, hours: null },
      ]);
    }
  };

  // BottomSheet için araç seçenekleri
  const vehicleOptions = useMemo(() => {
    return allVehicles.map((v: any) => ({
      value: v.id,
      label: v.plate,
      sublabel: `${v.brand} ${v.model}`,
    }));
  }, [allVehicles]);

  const handleVehicleSheetSelect = (vehicleId: string) => {
    toggleVehicle(vehicleId);
  };

  const updateVehicle = (vehicleId: string, field: string, value: string) => {
    setSelectedVehicles(
      selectedVehicles.map((v) =>
        v.vehicleId === vehicleId ? { ...v, [field]: value } : v
      )
    );
  };

  const toggleWork = (siteWorkId: string) => {
    const exists = selectedWorks.find((w) => w.siteWorkId === siteWorkId);
    if (exists) {
      setSelectedWorks(selectedWorks.filter((w) => w.siteWorkId !== siteWorkId));
    } else {
      setSelectedWorks([...selectedWorks, { siteWorkId, quantity: 0, notes: '' }]);
    }
  };

  const getRemainingQuantity = (siteWorkId: string): number | null => {
    const work = siteWorks.find((w) => w.id === siteWorkId);
    if (!work || work.targetQuantity === null) return null;
    return Math.max(0, work.targetQuantity - work.completedQuantity);
  };

  const updateWork = (siteWorkId: string, field: 'quantity' | 'notes', value: number | string) => {
    if (field === 'quantity') {
      const remaining = getRemainingQuantity(siteWorkId);
      if (remaining !== null && (value as number) > remaining) {
        value = remaining;
        toast.warning(`Kalan miktarı aşamazsınız (max: ${remaining.toLocaleString('tr-TR')})`);
      }
    }
    setSelectedWorks(
      selectedWorks.map((w) =>
        w.siteWorkId === siteWorkId ? { ...w, [field]: value } : w
      )
    );
  };

  // Fotoğraf ekleme
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const maxPhotos = 10;
    const remainingSlots = maxPhotos - selectedPhotos.length;

    if (newFiles.length > remainingSlots) {
      toast.warning(`En fazla ${maxPhotos} fotoğraf ekleyebilirsiniz`);
    }

    const filesToAdd = newFiles.slice(0, remainingSlots);

    // Preview oluştur
    filesToAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    setSelectedPhotos((prev) => [...prev, ...filesToAdd]);
    e.target.value = ''; // Input'u sıfırla
  };

  const removePhoto = (index: number) => {
    setSelectedPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Çalışılmadı kaydı oluştur
  const handleNoWork = async (reasonValue: string) => {
    const reason = NO_WORK_REASONS.find((r) => r.value === reasonValue);
    if (!reason) return;

    try {
      setSubmitting(true);
      await dailyRecordsAPI.create({
        recordDate: new Date(formData.recordDate),
        siteId: formData.siteId,
        notes: `ÇALIŞILMADI: ${reason.label}`,
        personnelEntries: [],
        vehicleEntries: [],
        workEntries: [],
      });

      toast.success(`${new Date(formData.recordDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} — Çalışılmadı olarak kaydedildi`);

      if (missingDays.length > 0) {
        // Eksik günleri güncelle — ilk günü listeden çıkar
        const remaining = missingDays.slice(1);
        setMissingDays(remaining);
        if (remaining.length > 0) {
          setFormData((prev) => ({ ...prev, recordDate: remaining[0] }));
        } else {
          setFormData((prev) => ({ ...prev, recordDate: new Date().toISOString().split('T')[0] }));
        }
      } else {
        // Eksik gün yoktu, bugün için çalışılmadı kaydı oluşturuldu
        navigate('/forman');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSubmitting(false);
      setShowNoWorkSheet(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.siteId) {
      toast.error('Lütfen şantiye seçin');
      return;
    }

    if (selectedPersonnel.length === 0 && selectedVehicles.length === 0 && selectedWorks.length === 0) {
      toast.error('En az bir personel, araç veya iş seçin');
      return;
    }

    // Araç sürücü kontrolü
    const missingDrivers = selectedVehicles.filter((v) => !v.driverId);
    if (missingDrivers.length > 0) {
      const plates = missingDrivers
        .map((v) => allVehicles.find((av: any) => av.id === v.vehicleId)?.plate)
        .filter(Boolean)
        .join(', ');
      toast.error(`Sürücü seçilmemiş: ${plates}`);
      return;
    }

    // Araç km/saat kontrolü
    const missingKmHours = selectedVehicles.filter((v) => {
      const vehicle = allVehicles.find((av: any) => av.id === v.vehicleId);
      if (vehicle?.isMachinery) return !v.hours;
      return !v.km;
    });
    if (missingKmHours.length > 0) {
      const plates = missingKmHours
        .map((v) => allVehicles.find((av: any) => av.id === v.vehicleId)?.plate)
        .filter(Boolean)
        .join(', ');
      toast.error(`Km/Saat girilmemiş: ${plates}`);
      return;
    }

    // İş miktarı kontrolü - 0 olan var mı?
    const invalidWorks = selectedWorks.filter((w) => !w.quantity || w.quantity <= 0);
    if (invalidWorks.length > 0) {
      toast.error('Seçilen işlerin miktarını girin');
      return;
    }

    try {
      setSubmitting(true);
      const response = await dailyRecordsAPI.create({
        recordDate: new Date(formData.recordDate),
        siteId: formData.siteId,
        notes: formData.notes || undefined,
        personnelEntries: selectedPersonnel,
        vehicleEntries: selectedVehicles.map((v) => ({
          ...v,
          km: v.km || undefined,
          hours: v.hours || undefined,
        })),
        workEntries: selectedWorks.map((w) => ({
          siteWorkId: w.siteWorkId,
          quantity: w.quantity,
          notes: w.notes || undefined,
        })),
      });

      const recordId = response.data.id;

      // Fotoğrafları yükle
      if (selectedPhotos.length > 0) {
        const uploadPromises = selectedPhotos.map((photo) => {
          const formDataPhoto = new FormData();
          formDataPhoto.append('file', photo);
          return dailyRecordsAPI.uploadPhoto(recordId, formDataPhoto);
        });

        try {
          await Promise.all(uploadPromises);
          toast.success(`Günlük kayıt ve ${selectedPhotos.length} fotoğraf eklendi!`);
        } catch (photoError) {
          console.error('Photo upload error:', photoError);
          toast.warning('Kayıt oluşturuldu ama bazı fotoğraflar yüklenemedi');
        }
      } else {
        toast.success('Günlük kayıt oluşturuldu!');
      }

      navigate('/forman');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const isPersonnelSelected = (id: string) =>
    selectedPersonnel.some((p) => p.personnelId === id);

  const isVehicleSelected = (id: string) =>
    selectedVehicles.some((v) => v.vehicleId === id);

  const getPersonnelWage = (id: string) =>
    selectedPersonnel.find((p) => p.personnelId === id)?.dailyWage || 1;

  const getVehicleUsage = (id: string) =>
    selectedVehicles.find((v) => v.vehicleId === id)?.usageType || 'FULL_DAY';

  const isWorkSelected = (id: string) =>
    selectedWorks.some((w) => w.siteWorkId === id);

  const getWorkQuantity = (id: string) =>
    selectedWorks.find((w) => w.siteWorkId === id)?.quantity || 0;

  const getVehicleDriver = (id: string) =>
    selectedVehicles.find((v) => v.vehicleId === id)?.driverId || '';

  const getVehicleDriverName = (vehicleId: string) => {
    const driverId = getVehicleDriver(vehicleId);
    if (!driverId) return null;
    const driver = getDriverOptions().find((d) => d.id === driverId);
    return driver?.name || null;
  };

  // Sürücü seçenekleri: Forman + seçili personeller
  const getDriverOptions = () => {
    const options: Array<{ id: string; name: string; type: 'USER' | 'PERSONNEL' }> = [];

    // Forman (current user)
    if (user) {
      options.push({
        id: user.id,
        name: `${user.firstName} ${user.lastName} (Ben)`,
        type: 'USER',
      });
    }

    // Seçili personeller (şantiye personelinden)
    selectedPersonnel.forEach((sp) => {
      const person = sitePersonnel.find((p) => p.id === sp.personnelId);
      if (person) {
        options.push({
          id: person.id,
          name: `${person.firstName} ${person.lastName}`,
          type: 'PERSONNEL',
        });
      }
    });

    return options;
  };

  // Sürücü seçildiğinde hem driverId hem driverType güncelle
  const updateVehicleDriver = (vehicleId: string, driverId: string) => {
    const driver = getDriverOptions().find((d) => d.id === driverId);
    setSelectedVehicles(
      selectedVehicles.map((v) =>
        v.vehicleId === vehicleId
          ? { ...v, driverId, driverType: driver?.type || '' }
          : v
      )
    );
  };

  // Herhangi bir BottomSheet açık mı? (bottom bar'ı gizlemek için)
  const anySheetOpen = showSiteSheet || showPersonnelSheet || showVehicleSheet || !!showDriverSheet || showNoWorkSheet || !!showOvertimeSheet;

  // Step navigation
  const currentStepIndex = STEPS.indexOf(currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === STEPS.length - 1;

  const goToNextStep = () => {
    if (!formData.siteId) {
      toast.error('Önce şantiye seçin');
      return;
    }

    // Araç step'inde sürücü ve km/saat kontrolü
    if (currentStep === 'arac' && selectedVehicles.length > 0) {
      const missingDriver = selectedVehicles.filter((v) => !v.driverId);
      if (missingDriver.length > 0) {
        const plates = missingDriver
          .map((v) => allVehicles.find((av: any) => av.id === v.vehicleId)?.plate)
          .filter(Boolean)
          .join(', ');
        toast.error(`Sürücü seçilmemiş: ${plates}`);
        return;
      }
      const missingKmHours = selectedVehicles.filter((v) => {
        const vehicle = allVehicles.find((av: any) => av.id === v.vehicleId);
        if (vehicle?.isMachinery) return !v.hours;
        return !v.km;
      });
      if (missingKmHours.length > 0) {
        const plates = missingKmHours
          .map((v) => allVehicles.find((av: any) => av.id === v.vehicleId)?.plate)
          .filter(Boolean)
          .join(', ');
        toast.error(`Km/Saat girilmemiş: ${plates}`);
        return;
      }
    }

    if (!isLastStep) {
      setCurrentStep(STEPS[currentStepIndex + 1]);
    }
  };

  const goToPrevStep = () => {
    if (!isFirstStep) {
      setCurrentStep(STEPS[currentStepIndex - 1]);
    }
  };

  return (
    <div className="forman-page">
      {/* Header */}
      <div className="forman-page-header">
        <button className="back-btn" onClick={() => navigate('/forman')}>
          <i className="ri-arrow-left-line"></i>
        </button>
        <h1>Günlük Kayıt</h1>
      </div>

      {/* Content */}
      <div className="forman-page-content">
        {/* Site & Date */}
        <div className="forman-field">
          <label>Şantiye</label>
          <button
            type="button"
            className={`forman-select-trigger ${formData.siteId ? 'has-value' : 'placeholder'}`}
            onClick={() => setShowSiteSheet(true)}
            disabled={selectedPersonnel.length > 0 || selectedVehicles.length > 0 || selectedWorks.length > 0}
          >
            <span className="forman-select-trigger-text">
              {formData.siteId
                ? sites.find((s) => s.id === formData.siteId)?.project?.name +
                  ' / ' +
                  sites.find((s) => s.id === formData.siteId)?.name
                : 'Şantiye seçin...'}
            </span>
            <i className="ri-arrow-down-s-line"></i>
          </button>
        </div>

        <div className="forman-field-row">
          <div className="forman-field flex-1">
            <label>Tarih</label>
            <input
              type="date"
              value={formData.recordDate}
              onChange={(e) =>
                setFormData({ ...formData, recordDate: e.target.value })
              }
              className="forman-input"
              disabled={!formData.siteId || missingDays.length > 0}
            />
          </div>
        </div>

        {/* Eksik gün uyarısı — şantiye/tarih alanında, personel seçilince gizle */}
        {formData.siteId && !checkingRecord && missingDays.length > 0 && !existingRecord && selectedPersonnel.length === 0 && (
          <div className="forman-missing-days-banner">
            <div className="forman-missing-days-header">
              <i className="ri-calendar-warning-line" style={{ fontSize: '1.5rem', color: 'var(--warning)' }}></i>
              <div>
                <strong>{missingDays.length} eksik gün var</strong>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', opacity: 0.85 }}>
                  Sıradaki eksik gün otomatik seçildi. Formu doldurup kaydedin.
                </p>
              </div>
            </div>
            <div className="forman-missing-date-badge">
              <i className="ri-calendar-line"></i>
              {new Date(missingDays[0] + 'T00:00:00').toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            <button
              type="button"
              className="forman-no-work-btn"
              onClick={() => setShowNoWorkSheet(true)}
              disabled={submitting}
            >
              <i className="ri-close-circle-line"></i>
              Bu gün çalışılmadı
            </button>
          </div>
        )}

        <BottomSheet
          isOpen={showSiteSheet}
          onClose={() => setShowSiteSheet(false)}
          title="Şantiye Seçin"
          options={sites.map((site) => ({
            value: site.id,
            label: site.name,
            sublabel: site.project?.name,
          }))}
          selectedValue={formData.siteId}
          onSelect={(value) => setFormData({ ...formData, siteId: value })}
          searchable
        />

        {/* Driver Selection BottomSheet */}
        <BottomSheet
          isOpen={!!showDriverSheet}
          onClose={() => setShowDriverSheet(null)}
          title="Sürücü Seçin"
          options={getDriverOptions().map((driver) => ({
            value: driver.id,
            label: driver.name,
            sublabel: driver.type === 'USER' ? 'Formen' : 'Personel',
          }))}
          selectedValue={showDriverSheet ? getVehicleDriver(showDriverSheet) : ''}
          onSelect={(value) => {
            if (showDriverSheet) {
              updateVehicleDriver(showDriverSheet, value);
            }
            setShowDriverSheet(null);
          }}
        />

        {/* Mesai Saati Seçimi BottomSheet */}
        <BottomSheet
          isOpen={!!showOvertimeSheet}
          onClose={() => setShowOvertimeSheet(null)}
          title="Mesai Saati"
          options={Array.from({ length: 12 }, (_, i) => ({
            value: String(i + 1),
            label: `${i + 1} Saat`,
          }))}
          selectedValue={showOvertimeSheet ? String(getOvertimeHours(showOvertimeSheet) || '') : ''}
          onSelect={(value) => {
            if (showOvertimeSheet) {
              setOvertimeHours(showOvertimeSheet, parseInt(value, 10));
            }
          }}
        />

        {/* Çalışılmadı butonu — şantiye/tarih sonrası, stepper öncesi */}
        {formData.siteId && !checkingRecord && !existingRecord && missingDays.length === 0 && (
          <div className="forman-no-work-bar">
            <button
              type="button"
              className="forman-no-work-btn"
              onClick={() => setShowNoWorkSheet(true)}
              disabled={submitting}
            >
              <i className="ri-close-circle-line"></i>
              Bu gün çalışılmadı
            </button>
          </div>
        )}

        {/* Step Indicator */}
        <div className="forman-stepper">
          {STEPS.map((step, index) => {
            const isActive = step === currentStep;
            const isCompleted = index < currentStepIndex;
            const stepCount =
              step === 'personel' ? selectedPersonnel.length :
              step === 'isler' ? selectedWorks.length :
              step === 'arac' ? selectedVehicles.length : 0;

            return (
              <div
                key={step}
                className={`forman-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="forman-step-dot">
                  {isCompleted ? (
                    <i className="ri-check-line"></i>
                  ) : (
                    index + 1
                  )}
                </div>
                <span className="forman-step-label">
                  {STEP_LABELS[step]}
                  {stepCount > 0 && <span className="forman-step-count">{stepCount}</span>}
                </span>
              </div>
            );
          })}
        </div>

        {/* Step Title */}
        <div className="forman-step-title">
          <h2>{STEP_LABELS[currentStep]}</h2>
          <span className="forman-step-subtitle">
            {currentStep === 'personel' && 'Bugün çalışan personeli seçin'}
            {currentStep === 'isler' && 'Yapılan işleri ve miktarları girin'}
            {currentStep === 'arac' && 'Kullanılan araçları seçin'}
            {currentStep === 'notlar' && 'Not ve fotoğraf ekleyin (opsiyonel)'}
          </span>
        </div>

        {/* Site Not Selected Warning */}
        {!formData.siteId && (
          <div className="forman-locked-overlay">
            <div className="forman-locked-content">
              <i className="ri-lock-line"></i>
              <p>Devam etmek için şantiye seçin</p>
              <button
                className="forman-btn-select-site"
                onClick={() => setShowSiteSheet(true)}
              >
                <i className="ri-building-4-line"></i>
                Şantiye Seç
              </button>
            </div>
          </div>
        )}

        {/* Kayıt kontrol ediliyor */}
        {formData.siteId && checkingRecord && (
          <div className="forman-locked-overlay">
            <div className="forman-locked-content">
              <div className="spinner-border text-primary"></div>
              <p>Kayıt kontrol ediliyor...</p>
            </div>
          </div>
        )}

        {/* Çalışılmadı sebep seçimi */}
        <BottomSheet
          isOpen={showNoWorkSheet}
          onClose={() => setShowNoWorkSheet(false)}
          title="Çalışılmama Sebebi"
          options={NO_WORK_REASONS.map((r) => ({
            value: r.value,
            label: r.label,
          }))}
          selectedValue=""
          onSelect={(value) => handleNoWork(value)}
        />

        {/* Bu gün için kayıt zaten var - kilitli */}
        {formData.siteId && !checkingRecord && existingRecord && (
          <div className="forman-locked-overlay">
            <div className="forman-locked-content forman-record-exists">
              <i className="ri-lock-2-fill"></i>
              <h3>Bu Gün İçin Kayıt Mevcut</h3>
              <p>
                <strong>{new Date(formData.recordDate).toLocaleDateString('tr-TR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</strong> tarihi için günlük kayıt girilmiş.
              </p>
              <div className="forman-record-info">
                <span>
                  <i className="ri-user-line"></i>
                  {existingRecord.createdBy.firstName} {existingRecord.createdBy.lastName}
                </span>
                <span className="forman-record-locked-badge">
                  <i className="ri-lock-line"></i>
                  Kilitli
                </span>
              </div>
              <p className="forman-record-hint">
                Değişiklik yapmak için yöneticinize başvurun.
              </p>
              <div className="forman-record-actions">
                <button
                  className="forman-locked-action-btn"
                  onClick={() => {
                    const prevDate = new Date(formData.recordDate);
                    prevDate.setDate(prevDate.getDate() - 1);
                    setFormData({ ...formData, recordDate: prevDate.toISOString().split('T')[0] });
                  }}
                >
                  Önceki Gün
                </button>
                <button
                  className="forman-locked-action-btn forman-locked-action-btn--home"
                  onClick={() => navigate('/forman')}
                >
                  Ana Sayfa
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Personnel Selection BottomSheet */}
        <BottomSheet
          isOpen={showPersonnelSheet}
          onClose={() => setShowPersonnelSheet(false)}
          title="Personel Seç"
          options={personnelOptions}
          multiSelect
          selectedValues={selectedPersonnel.map((sp) => sp.personnelId)}
          onSelect={handlePersonnelSheetSelect}
          searchable
        />

        {/* Personnel Step */}
        {formData.siteId && !checkingRecord && !existingRecord && currentStep === 'personel' && (
          <div className="forman-list">
            {loadingPersonnel ? (
              <div className="forman-empty">
                <div className="spinner-border spinner-border-sm text-primary"></div>
                <p>Yükleniyor...</p>
              </div>
            ) : sitePersonnel.length === 0 ? (
              <div className="forman-empty">
                <i className="ri-user-line"></i>
                <p>Aktif personel bulunamadı</p>
              </div>
            ) : (
              <>
                {/* Personel Seç butonu */}
                <button
                  type="button"
                  className="forman-select-personnel-btn"
                  onClick={() => setShowPersonnelSheet(true)}
                >
                  <i className="ri-user-add-line"></i>
                  <span>Personel Seç</span>
                  {selectedPersonnel.length > 0 && (
                    <span className="forman-select-personnel-count">{selectedPersonnel.length}</span>
                  )}
                  <i className="ri-arrow-right-s-line"></i>
                </button>

                {/* Seçili personel listesi — yevmiye ayarları */}
                {selectedPersonnel.length > 0 && (
                  <>
                    {/* Aylıkçılar */}
                    {selectedPersonnel
                      .map((sp) => sitePersonnel.find((p: any) => p.id === sp.personnelId))
                      .filter((p: any) => p?.employmentType === 'MONTHLY')
                      .length > 0 && (
                      <>
                        <div className="forman-group-label">Aylıkçı Personel</div>
                        {selectedPersonnel
                          .map((sp) => sitePersonnel.find((p: any) => p.id === sp.personnelId))
                          .filter((p: any) => p?.employmentType === 'MONTHLY')
                          .map((person: any) => (
                            <div key={person.id} className="forman-list-item selected">
                              <div className="forman-list-item-main">
                                <div className="forman-list-item-check">
                                  <i className="ri-checkbox-circle-fill"></i>
                                </div>
                                <div className="forman-list-item-info">
                                  <strong>{person.firstName} {person.lastName}</strong>
                                  <span>
                                    {person.title || 'İşçi'}
                                    {getOvertimeHours(person.id) > 0 && (
                                      <> — <strong>{getOvertimeHours(person.id)}s mesai</strong></>
                                    )}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="forman-remove-btn"
                                  onClick={() => togglePersonnel(person)}
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </div>
                              <div className="forman-list-item-options">
                                <label>Mesai:</label>
                                <div className="forman-wage-buttons">
                                  <button
                                    className={`forman-wage-btn ${getMonthlyMode(person.id) === 'half' ? 'active' : ''}`}
                                    onClick={() => setMonthlyMode(person.id, 'half')}
                                  >
                                    Yarım
                                  </button>
                                  <button
                                    className={`forman-wage-btn ${getMonthlyMode(person.id) === 'full' ? 'active' : ''}`}
                                    onClick={() => setMonthlyMode(person.id, 'full')}
                                  >
                                    Tam Gün
                                  </button>
                                  <button
                                    className={`forman-wage-btn ${getMonthlyMode(person.id) === 'overtime' ? 'active' : ''}`}
                                    onClick={() => setMonthlyMode(person.id, 'overtime')}
                                  >
                                    <i className="ri-time-line" style={{ marginRight: '0.25rem' }}></i>
                                    Mesai
                                    {getOvertimeHours(person.id) > 0 && ` (${getOvertimeHours(person.id)}s)`}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                      </>
                    )}

                    {/* Yevmiyeciler — yevmiye seçimi ile */}
                    {selectedPersonnel
                      .map((sp) => sitePersonnel.find((p: any) => p.id === sp.personnelId))
                      .filter((p: any) => p && p.employmentType !== 'MONTHLY')
                      .length > 0 && (
                      <>
                        <div className="forman-group-label">Yevmiyeci Personel</div>
                        {selectedPersonnel
                          .map((sp) => sitePersonnel.find((p: any) => p.id === sp.personnelId))
                          .filter((p: any) => p && p.employmentType !== 'MONTHLY')
                          .map((person: any) => (
                            <div key={person.id} className="forman-list-item selected">
                              <div className="forman-list-item-main">
                                <div className="forman-list-item-check">
                                  <i className="ri-checkbox-circle-fill"></i>
                                </div>
                                <div className="forman-list-item-info">
                                  <strong>{person.firstName} {person.lastName}</strong>
                                  <span>{person.title || 'İşçi'}</span>
                                </div>
                                <button
                                  type="button"
                                  className="forman-remove-btn"
                                  onClick={() => togglePersonnel(person)}
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </div>
                              <div className="forman-list-item-options">
                                <label>Yevmiye:</label>
                                <div className="forman-wage-buttons">
                                  {[0.5, 1, 1.5, 2].map((wage) => (
                                    <button
                                      key={wage}
                                      className={`forman-wage-btn ${
                                        getPersonnelWage(person.id) === wage ? 'active' : ''
                                      }`}
                                      onClick={() => updatePersonnelWage(person.id, wage)}
                                    >
                                      {wage}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Works List */}
        {formData.siteId && !checkingRecord && !existingRecord && currentStep === 'isler' && (
          <div className="forman-list">
            {loadingSiteWorks ? (
              <div className="forman-empty">
                <div className="spinner-border spinner-border-sm text-primary"></div>
                <p>Yükleniyor...</p>
              </div>
            ) : siteWorks.length === 0 ? (
              <div className="forman-empty">
                <i className="ri-tools-line"></i>
                <p>Bu şantiyeye iş atanmamış</p>
              </div>
            ) : (
              siteWorks.map((work) => (
                <div
                  key={work.id}
                  className={`forman-list-item ${
                    isWorkSelected(work.id) ? 'selected' : ''
                  }`}
                >
                  <div
                    className="forman-list-item-main"
                    onClick={() => toggleWork(work.id)}
                  >
                    <div className="forman-list-item-check">
                      {isWorkSelected(work.id) ? (
                        <i className="ri-checkbox-circle-fill"></i>
                      ) : (
                        <i className="ri-checkbox-blank-circle-line"></i>
                      )}
                    </div>
                    <div className="forman-list-item-info">
                      <strong>{work.workType.name}</strong>
                      <span>
                        Yapılan: {work.completedQuantity.toLocaleString('tr-TR')} {work.workType.unit}
                        {work.targetQuantity && (
                          <> / {work.targetQuantity.toLocaleString('tr-TR')} {work.workType.unit} (%{work.progressPercentage || 0})</>
                        )}
                      </span>
                    </div>
                  </div>
                  {isWorkSelected(work.id) && (() => {
                    const remaining = getRemainingQuantity(work.id);
                    return (
                      <div className="forman-list-item-options forman-work-options">
                        <div className="forman-work-input">
                          <label>Miktar ({work.workType.unit}):</label>
                          <input
                            type="number"
                            className="forman-input"
                            placeholder="0"
                            min="0"
                            step="0.01"
                            max={remaining !== null ? remaining : undefined}
                            value={getWorkQuantity(work.id) || ''}
                            onChange={(e) =>
                              updateWork(work.id, 'quantity', parseFloat(e.target.value) || 0)
                            }
                          />
                        </div>
                        {remaining !== null && (
                          <span className="forman-work-remaining">
                            Kalan: {remaining.toLocaleString('tr-TR')} {work.workType.unit}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))
            )}
          </div>
        )}

        {/* Vehicle Selection BottomSheet */}
        <BottomSheet
          isOpen={showVehicleSheet}
          onClose={() => setShowVehicleSheet(false)}
          title="Araç Seç"
          options={vehicleOptions}
          multiSelect
          selectedValues={selectedVehicles.map((sv) => sv.vehicleId)}
          onSelect={handleVehicleSheetSelect}
          searchable
        />

        {/* Vehicle Step */}
        {formData.siteId && !checkingRecord && !existingRecord && currentStep === 'arac' && (
          <div className="forman-list">
            {allVehicles.length === 0 ? (
              <div className="forman-empty">
                <i className="ri-truck-line"></i>
                <p>Aktif araç bulunamadı</p>
              </div>
            ) : (
              <>
                {/* Araç Seç butonu */}
                <button
                  type="button"
                  className="forman-select-personnel-btn"
                  onClick={() => setShowVehicleSheet(true)}
                >
                  <i className="ri-truck-line"></i>
                  <span>Araç Seç</span>
                  {selectedVehicles.length > 0 && (
                    <span className="forman-select-personnel-count">{selectedVehicles.length}</span>
                  )}
                  <i className="ri-arrow-right-s-line"></i>
                </button>

                {/* Seçili araçlar — kullanım + sürücü ayarları */}
                {selectedVehicles.map((sv) => {
                  const vehicle = allVehicles.find((v: any) => v.id === sv.vehicleId);
                  if (!vehicle) return null;
                  return (
                    <div key={vehicle.id} className="forman-list-item selected">
                      <div className="forman-list-item-main">
                        <div className="forman-list-item-check">
                          <i className="ri-checkbox-circle-fill"></i>
                        </div>
                        <div className="forman-list-item-info">
                          <strong>{vehicle.plate}</strong>
                          <span>{vehicle.brand} {vehicle.model}</span>
                        </div>
                        <button
                          type="button"
                          className="forman-remove-btn"
                          onClick={() => toggleVehicle(vehicle.id)}
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                      <div className="forman-list-item-options forman-vehicle-options">
                        <div className="forman-vehicle-option-row">
                          <label>Kullanım:</label>
                          <div className="forman-usage-buttons">
                            {[
                              { value: 'FULL_DAY', label: 'Tam Gün' },
                              { value: 'HALF_DAY', label: 'Yarım' },
                            ].map((opt) => (
                              <button
                                key={opt.value}
                                className={`forman-usage-btn ${
                                  getVehicleUsage(vehicle.id) === opt.value ? 'active' : ''
                                }`}
                                onClick={() => updateVehicle(vehicle.id, 'usageType', opt.value)}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="forman-vehicle-option-row forman-driver-row">
                          <label>Sürücü:</label>
                          <button
                            type="button"
                            className={`forman-driver-trigger ${getVehicleDriver(vehicle.id) ? 'has-value' : ''}`}
                            onClick={() => setShowDriverSheet(vehicle.id)}
                          >
                            <i className="ri-user-line"></i>
                            <span>{getVehicleDriverName(vehicle.id) || 'Sürücü seç...'}</span>
                            <i className="ri-arrow-down-s-line"></i>
                          </button>
                          {getDriverOptions().length === 1 && !getVehicleDriver(vehicle.id) && (
                            <span className="forman-driver-hint">
                              Daha fazla sürücü için personel ekleyin
                            </span>
                          )}
                        </div>
                        <div className="forman-vehicle-option-row">
                          <input
                            type="number"
                            className="forman-input"
                            placeholder={vehicle.isMachinery ? 'Saat' : 'Km'}
                            min="0"
                            step={vehicle.isMachinery ? '0.5' : '1'}
                            value={vehicle.isMachinery ? (sv.hours ?? '') : (sv.km ?? '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (vehicle.isMachinery) {
                                updateVehicle(vehicle.id, 'hours', val ? parseFloat(val) : null as any);
                              } else {
                                updateVehicle(vehicle.id, 'km', val ? parseInt(val, 10) : null as any);
                              }
                            }}
                            style={{ maxWidth: '120px' }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* Notes & Photos */}
        {formData.siteId && !checkingRecord && !existingRecord && currentStep === 'notlar' && (
          <div className="forman-notes-section">
            <div className="forman-field">
              <label>
                <i className="ri-sticky-note-line"></i>
                Günlük Not
              </label>
              <textarea
                className="forman-textarea"
                placeholder="Bugün yapılan işler, sorunlar, notlar..."
                rows={4}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>

            <div className="forman-field">
              <label>
                <i className="ri-camera-line"></i>
                Fotoğraflar
                {selectedPhotos.length > 0 && (
                  <span className="forman-photo-count">{selectedPhotos.length}/10</span>
                )}
              </label>

              {/* Photo Previews */}
              {photoPreviews.length > 0 && (
                <div className="forman-photo-grid">
                  {photoPreviews.map((preview, index) => (
                    <div key={index} className="forman-photo-item">
                      <img src={preview} alt={`Fotoğraf ${index + 1}`} />
                      <button
                        type="button"
                        className="forman-photo-remove"
                        onClick={() => removePhoto(index)}
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Photo Buttons */}
              {selectedPhotos.length < 10 && (
                <div className="forman-photo-actions">
                  <label className="forman-photo-btn">
                    <i className="ri-camera-line"></i>
                    <span>Kamera</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoSelect}
                      hidden
                    />
                  </label>
                  <label className="forman-photo-btn">
                    <i className="ri-image-line"></i>
                    <span>Galeri</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoSelect}
                      hidden
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Bottom Actions */}
      <div className={`forman-bottom-actions forman-wizard-actions ${anySheetOpen ? 'sheet-open' : ''}`}>
        {!isFirstStep ? (
          <button className="forman-btn-secondary" onClick={goToPrevStep}>
            <i className="ri-arrow-left-line"></i>
            Geri
          </button>
        ) : (
          <div className="forman-summary-mini">
            <span><i className="ri-team-line"></i> {selectedPersonnel.length}</span>
            <span><i className="ri-tools-line"></i> {selectedWorks.length}</span>
            <span><i className="ri-truck-line"></i> {selectedVehicles.length}</span>
          </div>
        )}

        {isLastStep ? (
          <button
            className="forman-btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !formData.siteId}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Kaydediliyor...
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                Kaydet
              </>
            )}
          </button>
        ) : (
          <button
            className="forman-btn-primary"
            onClick={goToNextStep}
            disabled={!formData.siteId}
          >
            İleri
            <i className="ri-arrow-right-line"></i>
          </button>
        )}
      </div>
    </div>
  );
};

export default FormanDailyRecord;
